import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextSelector } from 'use-context-selector'

import { AppContext } from '../../components/AppState'
import { AppActionType } from '../../components/AppState/types'
import Content from '../../components/Content'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Segment from '../../components/Segment'
import { useJmtNetworksQuery, useSearchTalentLazyQuery } from '../../lib/apollo/types'
import { useUser } from '../../providers/Auth/hooks'

import MarketplaceSearchHistory from './History'
import MarketplaceKeywordSearch from './Keyword'
import MarketplacePlayertypesSearch from './Similar/PlayerTypes'
import MarketplaceRolemodelSearch from './Similar/RoleModel'

enum TABS {
    RoleModels,
    PrefapSearch,
    Search,
    RecentSearches,
}

const MarketplaceSearch: React.FC = () => {
    const me = useUser()
    const dispatch = useContextSelector(AppContext, state => state.dispatch)
    const { t } = useTranslation()
    const [searchString, setSearchString] = useState<{ value?: string, event: any }>({ value: '', event: null })

    const networks = useJmtNetworksQuery()

    const marketplaceNetwork = useMemo(() =>
        networks?.data?.jmtNetworks.JMTnetworks.collection.find((network) => network.caption === 'Join.My.Talent.Engine.Marketplace')
    , [networks])

    const [lazySearchTalentQuery, { data: { searchTalent = undefined } = {} }] = useSearchTalentLazyQuery()
    // Active Segment
    const [activeSegment, setActiveSegment] = useState(TABS.RoleModels)

    const tabs = useMemo(() => Object.values(TABS)
        .filter((key: number | string) => typeof key === 'number')
        .map(tab => ({
            tab: t(`marketplace.search.${tab}`),
        }))
    , [t])

    const getChild = (): ReactNode => {
        switch (activeSegment) {
            case TABS.RoleModels: return <MarketplaceRolemodelSearch />
            case TABS.PrefapSearch: return <MarketplacePlayertypesSearch />
            case TABS.Search: return (
                <MarketplaceKeywordSearch />
            )
            case TABS.RecentSearches: {
                return (
                    <MarketplaceSearchHistory
                        onClick={value => {
                            setSearchString(() => ({ value, event: null }))
                            setActiveSegment(() => TABS.Search)
                            if(me) {
                                lazySearchTalentQuery({
                                    variables: {
                                        query: value,
                                        networkId: marketplaceNetwork?.id ?? '',
                                    },
                                })
                            }
                        }}
                    />
                )
            }
            default: return null
        }
    }

    useEffect(() => {
        if (!searchString?.value || !searchTalent?.collection || searchTalent?.collection.length === 0) return
        dispatch({
            type: AppActionType.MarketplaceSearchHistoryAdd,
            payload: {
                type: 'search',
                data: searchString.value,
            },
        })
    }, [dispatch, searchString.value, searchTalent?.collection])

    return (
        <Page
            trackingTitle='marketplace-search'
            className='marketplace-search'
        >
            <Header
                title={t('marketplace.searchButton')}
            />
            <Content
                fullscreen
            >
                <Segment
                    value={activeSegment.toString()}
                    onIonChange={(e) => {
                        if(+(e.detail.value ?? '0') === 2) {
                            setActiveSegment(() => TABS.Search)
                        }
                        if(+(e.detail.value ?? '0') === 3) {
                            setActiveSegment(() => TABS.RecentSearches)
                        }
                        setActiveSegment(() => +(e.detail.value ?? '0'))
                    }}
                    tabs={tabs}
                />

                {getChild()}
            </Content>
        </Page>
    )
}

export default MarketplaceSearch
